import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, Chip, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';

import useUserStore from '../../../../../common/src/stores/useUserStore';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import { cn } from '../../../../../common/src/utils/cn';
import {
  getArrivalData,
  refreshArrivalTracking,
} from 'common/src/api/shipment-arrivals';
import { getShipmentShipTripsByShipmentIds } from 'common/src/api/shipments';
import type { ShipmentBox } from 'common/src/utils/types';
import { exporterUsesFrutic } from 'common/src/utils/frutic-functions';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
} from '../../../components/ui';
import ShipmentDetailsModal from '../../../components/shipments/ShipmentDetailsModal';
import { useShipmentDetails } from '../../../hooks/useShipmentDetails';
import useReceiverStore from '../../../store/useReceiverStore';
import { canRegisterShipmentPrices } from '../../../utils/shipment-price-access';
import { buildArrivals, canRefreshArrival } from '../arrival-manager/model';
import { useScopedRequest } from '../arrival-manager/use-scoped-request';
import {
  buildScheduleTrips,
  sortScheduleTrips,
  tripsInWindow,
  type ScheduleModeFilter,
  type ScheduleSort,
  type ScheduleTrip,
} from './model';

const MONTHS_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sept',
  'oct',
  'nov',
  'dic',
];
const MONTHS_LONG = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];
/** Default window: three weeks back, seven weeks ahead. */
const WINDOW = { from: -21, to: 49 };
// Theme colors (they follow the species theme and dark mode).
const COLOR = {
  arrived: 'hsl(var(--nextui-success))',
  pending: 'hsl(var(--nextui-default-400) / .18)',
  unconfirmed: 'hsl(var(--nextui-default-400))',
  hatch:
    'repeating-linear-gradient(45deg, hsl(var(--nextui-default-400) / .6) 0 3px, hsl(var(--nextui-default-400) / .12) 3px 7px)',
};
const LEG_COLORS = [
  ['hsl(var(--nextui-primary))', 'hsl(var(--nextui-primary) / .28)'],
  ['hsl(var(--nextui-secondary))', 'hsl(var(--nextui-secondary) / .28)'],
] as const;

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const dateForOffset = (offset: number) => {
  const date = startOfToday();
  date.setDate(date.getDate() + offset);
  return date;
};

const formatOffset = (offset: number | null, includeYear = false) => {
  if (offset === null) return '—';
  const date = dateForOffset(offset);
  const value = `${String(date.getDate()).padStart(2, '0')} ${MONTHS_SHORT[date.getMonth()]}`;
  return includeYear ? `${value} ${date.getFullYear()}` : value;
};

const formatKg = (kg: number) =>
  `${new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(kg)} kg`;

interface TimelineScale {
  start: number;
  end: number;
  span: number;
  percent: (offset: number) => number;
  /** Whether today falls inside the axis (past seasons end before it). */
  showsToday: boolean;
}

/**
 * The axis spans the shown shipments of the selected season and species, from
 * the first departure to the last arrival. The coming-weeks view also keeps
 * today in range.
 */
const createScale = (
  trips: ScheduleTrip[],
  showOriginalPlan: boolean,
  includeToday: boolean
): TimelineScale => {
  const starts = trips.flatMap((trip) => [
    trip.etd,
    ...(showOriginalPlan && trip.plannedEtd !== null ? [trip.plannedEtd] : []),
  ]);
  const ends = trips.flatMap((trip) => [
    trip.eta,
    ...(showOriginalPlan && trip.plannedEta !== null ? [trip.plannedEta] : []),
  ]);
  const today = includeToday ? [0] : [];
  const first = Math.min(...today, ...starts);
  const last = Math.max(...today, ...ends);
  // Room for the date labels drawn beside the bars.
  const start = first - Math.max(2, Math.ceil((last - first) * 0.03));
  const end = last + Math.max(4, Math.ceil((last - first) * 0.06));
  const span = end - start;
  return {
    start,
    end,
    span,
    percent: (offset) => ((offset - start) / span) * 100,
    showsToday: start <= 0 && end >= 0,
  };
};

const buildMonthBlocks = (scale: TimelineScale) => {
  const months: Array<{
    key: string;
    label: string;
    start: number;
    dayCount: number;
  }> = [];
  let currentKey = '';

  for (let offset = scale.start; offset <= scale.end; offset += 1) {
    const date = dateForOffset(offset);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (key !== currentKey) {
      months.push({
        key,
        label: MONTHS_LONG[date.getMonth()],
        start: offset,
        dayCount: 0,
      });
      currentKey = key;
    }
    months[months.length - 1].dayCount += 1;
  }

  return months.map((month) => ({
    ...month,
    left: scale.percent(month.start),
    width: (month.dayCount / scale.span) * 100,
  }));
};

const buildWeekMarkers = (scale: TimelineScale) => {
  const markers: Array<{ offset: number; left: number; label: string }> = [];
  // Long ranges label fewer Mondays so the labels never overlap.
  const every = scale.span > 120 ? 4 : scale.span > 70 ? 2 : 1;
  let monday = 0;
  for (let offset = scale.start; offset <= scale.end; offset += 1) {
    const date = dateForOffset(offset);
    if (date.getDay() !== 1) continue;
    monday += 1;
    if ((monday - 1) % every) continue;
    const left = scale.percent(offset);
    const closeToToday =
      scale.showsToday && Math.abs(left - scale.percent(0)) < 3.5;
    const closeToStart = left < 2;
    markers.push({
      offset,
      left,
      label:
        closeToToday || closeToStart
          ? ''
          : `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`,
    });
  }
  return markers;
};

const LegendItem = ({
  label,
  kind,
}: {
  label: string;
  kind:
    | 'transit'
    | 'arrived'
    | 'unconfirmed'
    | 'pending'
    | 'transfer'
    | 'today';
}) => (
  <span className='flex items-center gap-2 text-xs text-default-500'>
    <span
      className={cn(
        'inline-block shrink-0',
        kind === 'today'
          ? 'h-4 w-0.5 rounded bg-brand-700'
          : 'h-2.5 w-5 rounded-full',
        kind === 'transit' && 'bg-primary',
        kind === 'arrived' && 'bg-success',
        kind === 'unconfirmed' && 'bg-default-400',
        kind === 'pending' &&
          'border border-dashed border-default-500 bg-default-100',
        kind === 'transfer' &&
          'rounded-sm border border-default-400 bg-default-200'
      )}
      style={
        kind === 'transfer'
          ? {
              backgroundImage: COLOR.hatch,
            }
          : undefined
      }
    />
    {label}
  </span>
);

const TimelineHeader = ({
  scale,
  months,
  weeks,
}: {
  scale: TimelineScale;
  months: ReturnType<typeof buildMonthBlocks>;
  weeks: ReturnType<typeof buildWeekMarkers>;
}) => (
  <div className='grid grid-cols-[170px_1fr] border-b border-divider bg-default-50 sm:grid-cols-[300px_1fr]'>
    <div className='sticky left-0 z-20 self-stretch bg-default-50 px-3 pb-3 pt-8 text-xs font-semibold uppercase tracking-wide text-default-500 sm:px-5'>
      Nave o vuelo
    </div>
    <div className='relative h-14 overflow-hidden'>
      {months.map((month) => (
        <span
          key={month.key}
          className='absolute top-2 h-4 overflow-hidden whitespace-nowrap border-l border-divider pl-2 text-[11px] font-bold uppercase tracking-wide text-default-600'
          style={{ left: `${month.left}%`, width: `${month.width}%` }}
        >
          {month.label}
        </span>
      ))}
      {weeks.map((week) => (
        <span
          key={week.offset}
          className='absolute bottom-1.5 -translate-x-1/2 whitespace-nowrap text-[11px] text-default-500'
          style={{ left: `${week.left}%` }}
        >
          {week.label}
        </span>
      ))}
      {scale.showsToday && (
        <span
          className='absolute bottom-1 -translate-x-1/2 rounded-full bg-brand-700 px-2 py-0.5 text-[10px] font-bold text-white'
          style={{ left: `${scale.percent(0)}%` }}
        >
          Hoy
        </span>
      )}
    </div>
  </div>
);

const timelineBackground = (
  start: number,
  end: number,
  arrived: boolean,
  pending: boolean,
  unconfirmed: boolean,
  pastColor: string,
  futureColor: string
) => {
  if (arrived) return COLOR.arrived;
  if (pending) return COLOR.pending;
  // ETA already passed but nothing confirmed the arrival.
  if (unconfirmed) return COLOR.unconfirmed;
  const progress =
    0 >= end ? 100 : 0 <= start ? 0 : ((0 - start) / (end - start)) * 100;
  if (progress >= 100) return pastColor;
  if (progress <= 0) return futureColor;
  return `linear-gradient(90deg, ${pastColor} ${progress.toFixed(1)}%, ${futureColor} ${progress.toFixed(1)}%)`;
};

const ETA_LABEL: Record<NonNullable<ScheduleTrip['etaSource']>, string> = {
  actual: 'Arribo confirmado',
  tracking: 'ETA del tracking',
  planned: 'ETA planificada',
};

interface TripRowProps {
  trip: ScheduleTrip;
  scale: TimelineScale;
  showOriginalPlan: boolean;
  expanded: boolean;
  onToggle: () => void;
  onOpenShipment: (shipment: ScheduleTrip['shipments'][number]) => void;
}

const TripRow = ({
  trip,
  scale,
  showOriginalPlan,
  expanded,
  onToggle,
  onOpenShipment,
}: TripRowProps) => {
  const arrived = trip.arrived;
  const pending = !arrived && trip.etd > 0;
  const inTransit = !arrived && !pending && trip.eta >= 0;
  const unconfirmed = !arrived && trip.eta < 0;
  const delay = trip.delay ?? 0;
  // At least 10px, so one-day flights stay visible on a long axis.
  const widthFor = (start: number, end: number) =>
    `max(10px, ${((Math.max(end - start, 0) / scale.span) * 100).toFixed(3)}%)`;
  const air = trip.mode === 'air';
  const units = trip.shipments.length;
  const details: Array<[string, string, string?]> = [
    [air ? 'AWB' : 'Nave', trip.title, 'font-semibold'],
    [air ? 'Aerolínea' : 'Naviera', trip.carrier],
    [
      air ? 'Aeropuerto de destino' : 'Puerto de destino',
      `${trip.flag} ${trip.portName}`.trim(),
    ],
    [air ? 'Embarques' : 'Contenedores', String(units), 'font-semibold'],
    ['ETD original', formatOffset(trip.plannedEtd, true)],
    [
      'ETD real',
      formatOffset(trip.etd, true),
      trip.plannedEtd !== null && trip.etd !== trip.plannedEtd
        ? 'text-warning'
        : '',
    ],
    ['ETA original', formatOffset(trip.plannedEta, true)],
    [
      trip.etaSource ? ETA_LABEL[trip.etaSource] : 'ETA',
      formatOffset(trip.eta, true),
      delay > 0
        ? delay >= 2
          ? 'font-semibold text-danger'
          : 'font-semibold text-warning'
        : '',
    ],
  ];

  return (
    <div className='border-t border-divider'>
      <button
        type='button'
        aria-expanded={expanded}
        className={cn(
          'group grid w-full grid-cols-[170px_1fr] text-left transition-colors hover:bg-default-100/70 sm:grid-cols-[300px_1fr]',
          expanded && 'bg-default-100/60'
        )}
        onClick={onToggle}
      >
        <div
          className={cn(
            'sticky left-0 z-20 min-w-0 px-3 py-3 transition-colors sm:px-5',
            expanded
              ? 'bg-default-100'
              : 'bg-content1 group-hover:bg-default-100'
          )}
        >
          <div className='flex items-start gap-2'>
            <Icon
              icon={air ? 'tabler:plane' : 'tabler:ship'}
              width={15}
              className='mt-0.5 shrink-0 text-default-400'
            />
            <span
              className={cn(
                'text-pretty text-sm font-semibold leading-snug',
                air && 'font-mono'
              )}
            >
              {trip.title}
            </span>
            {!air && (
              <Chip size='sm' variant='flat' className='shrink-0'>
                {units} cont
              </Chip>
            )}
          </div>
          <p className='mt-1 pl-[23px] text-xs text-default-500'>
            {trip.carrier} · {trip.flag ? `${trip.flag} ` : ''}
            {trip.pod}
          </p>
          <span className='sr-only'>
            {arrived
              ? 'Arribada'
              : unconfirmed
                ? 'Sin confirmar arribo'
                : pending
                  ? 'Por zarpar'
                  : 'En tránsito'}
            . ETD {formatOffset(trip.etd, true)}, ETA{' '}
            {formatOffset(trip.eta, true)}
            {delay > 0 ? `, ${delay} días de atraso` : ''}.
          </span>
        </div>

        <div className='relative h-16 overflow-hidden'>
          {scale.showsToday && (
            <span
              className='pointer-events-none absolute inset-y-0 z-10 w-px bg-brand-500/60'
              style={{ left: `${scale.percent(0)}%` }}
              aria-hidden
            />
          )}
          {showOriginalPlan &&
            trip.plannedEtd !== null &&
            trip.plannedEta !== null && (
              <Tooltip
                content={`Plan original · ETD ${formatOffset(trip.plannedEtd, true)} → ETA ${formatOffset(trip.plannedEta, true)}`}
              >
                <span
                  className='absolute top-3 h-2 rounded-full border border-dashed border-default-500 bg-default-100'
                  style={{
                    left: `${scale.percent(trip.plannedEtd)}%`,
                    width: widthFor(trip.plannedEtd, trip.plannedEta),
                  }}
                />
              </Tooltip>
            )}

          {trip.legs.length > 1 ? (
            trip.legs.map((leg, index) => {
              const next = trip.legs[index + 1];
              const [past, future] = LEG_COLORS[index % 2];
              return (
                <span key={`${leg.vessel}-${index}`}>
                  <Tooltip
                    content={`Tramo ${index + 1} · ${leg.vessel} · ${formatOffset(leg.start, true)} → ${leg.destination || 'destino'} ${formatOffset(leg.end, true)}`}
                  >
                    <span
                      className={cn(
                        'absolute top-7 h-4',
                        index === 0 && 'rounded-l-full',
                        !next && 'rounded-r-full',
                        pending && 'border border-dashed border-default-500'
                      )}
                      style={{
                        left: `${scale.percent(leg.start)}%`,
                        width: widthFor(leg.start, leg.end),
                        background: timelineBackground(
                          leg.start,
                          leg.end,
                          arrived,
                          pending,
                          unconfirmed,
                          past,
                          future
                        ),
                      }}
                    />
                  </Tooltip>
                  {next && next.start > leg.end && (
                    <Tooltip
                      content={`Transbordo en ${leg.destination || 'puerto intermedio'} · ${next.start - leg.end} días en puerto`}
                    >
                      <span
                        className='absolute top-7 h-4 border-y border-default-400'
                        style={{
                          left: `${scale.percent(leg.end)}%`,
                          width: widthFor(leg.end, next.start),
                          backgroundImage: COLOR.hatch,
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
              );
            })
          ) : (
            <Tooltip
              content={`${trip.etaSource === 'planned' ? 'Planificado' : 'Real'} · ETD ${formatOffset(trip.etd, true)} → ETA ${formatOffset(trip.eta, true)}`}
            >
              <span
                className={cn(
                  'absolute top-7 h-4 rounded-full',
                  pending && 'border border-dashed border-default-500'
                )}
                style={{
                  left: `${scale.percent(trip.etd)}%`,
                  width: widthFor(trip.etd, trip.eta),
                  background: timelineBackground(
                    trip.etd,
                    trip.eta,
                    arrived,
                    pending,
                    unconfirmed,
                    LEG_COLORS[0][0],
                    LEG_COLORS[0][1]
                  ),
                }}
              />
            </Tooltip>
          )}

          {scale.percent(trip.etd) >= 4 && (
            <span
              className='absolute top-9 -translate-x-[calc(100%+8px)] -translate-y-1/2 whitespace-nowrap text-xs text-default-500'
              style={{ left: `${scale.percent(trip.etd)}%` }}
            >
              {formatOffset(trip.etd)}
            </span>
          )}
          <span
            className='absolute top-9 flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-default-600'
            style={{ left: `calc(${scale.percent(trip.eta)}% + 8px)` }}
          >
            {formatOffset(trip.eta)}
            {delay > 0 && (
              <Chip
                size='sm'
                variant='flat'
                color={delay >= 2 ? 'danger' : 'warning'}
                className='h-5'
              >
                +{delay}d{arrived ? ' tarde' : ''}
              </Chip>
            )}
          </span>
          {inTransit && scale.showsToday && (
            <Tooltip content='Posición estimada hoy'>
              <span
                className='absolute top-9 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background ring-4 ring-brand-500/50'
                style={{ left: `${scale.percent(0)}%` }}
              />
            </Tooltip>
          )}
        </div>
      </button>

      {expanded && (
        <div className='border-t border-dashed border-divider bg-default-50/60 px-5 py-5'>
          <div className='grid gap-x-7 gap-y-5 sm:grid-cols-2 lg:grid-cols-4'>
            {details.map(([label, value, className]) => (
              <div key={label}>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                  {label}
                </p>
                <p className={cn('mt-1 text-sm', className)}>{value}</p>
              </div>
            ))}
            {trip.legs.map((leg, index) => (
              <div key={`leg-${index}`}>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                  Tramo {index + 1}
                </p>
                <p className='mt-1 text-sm'>{leg.vessel}</p>
                <p className='mt-0.5 text-xs text-default-400'>
                  {formatOffset(leg.start, true)} →{' '}
                  {[leg.destination, formatOffset(leg.end, true)]
                    .filter(Boolean)
                    .join(' ')}
                </p>
              </div>
            ))}
          </div>
          <div className='mt-5 overflow-hidden rounded-lg border border-divider bg-background'>
            {trip.shipments.map((shipment) => (
              <div
                key={`${shipment.shipmentId}-${shipment.container}`}
                className='flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-divider px-4 py-2.5 text-sm last:border-b-0'
              >
                <span className='font-semibold'>{shipment.reference}</span>
                <span className='font-mono text-xs text-default-500'>
                  {shipment.container}
                </span>
                <span className='min-w-0 flex-1 truncate text-default-500'>
                  {shipment.client}
                </span>
                <span className='text-xs text-default-500'>
                  {formatKg(shipment.kg)}
                </span>
                <Button
                  size='sm'
                  variant='light'
                  endContent={<Icon icon='tabler:arrow-right' width={15} />}
                  onPress={() => onOpenShipment(shipment)}
                >
                  Ver despacho
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SchedulePage = () => {
  const { userProfile, session } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;
  const usesFrutic = exporterUsesFrutic(userProfile?.exporter, selectedSeason);
  const { exporterReceivers, fetchExporterReceivers } = useReceiverStore();
  useEffect(() => {
    if (exporterId)
      void fetchExporterReceivers(exporterId).catch(() => undefined);
  }, [exporterId, fetchExporterReceivers]);
  const getReceiverLabel = useCallback(
    (box: Pick<ShipmentBox, 'exporter_receiver_id' | 'receiver'>) =>
      exporterReceivers.find(
        (receiver) =>
          receiver.id === box.exporter_receiver_id &&
          receiver.exporter_id === exporterId
      )?.name ||
      box.receiver ||
      undefined,
    [exporterId, exporterReceivers]
  );

  const scope =
    session?.user.id && exporterId && selectedSeason && selectedSpecies?.id
      ? JSON.stringify([
          session.user.id,
          userProfile?.id,
          exporterId,
          selectedSeason,
          selectedSpecies.id,
        ])
      : null;
  const load = useCallback(
    async (signal: AbortSignal) => {
      if (!exporterId || !selectedSeason || !selectedSpecies?.id)
        throw new Error('Selecciona temporada y especie');
      const data = await getArrivalData(
        exporterId,
        selectedSeason,
        selectedSpecies.id,
        signal
      );
      // Legs only matter for tracked shipments; without them a trip is direct.
      const tracked = [
        ...new Set(
          data.cargo
            .filter(
              (box) =>
                (data.tracking[box.reference_number?.trim() ?? ''] ?? [])
                  .length > 0
            )
            .map((box) => Number(box.shipment_id))
        ),
      ];
      const shipTrips = await getShipmentShipTripsByShipmentIds(
        tracked,
        signal
      ).catch(() => []);
      return { data, shipTrips, loadedAt: new Date() };
    },
    [exporterId, selectedSeason, selectedSpecies?.id]
  );
  const request = useScopedRequest(scope, load);
  const { reload } = request;
  const reloadSchedule = useCallback(() => {
    void reload();
  }, [reload]);
  const details = useShipmentDetails({
    scope,
    exporterId,
    season: selectedSeason,
    speciesId: selectedSpecies?.id,
    usesFrutic,
    onHeaderSaved: reloadSchedule,
    onTrackingUpdated: reloadSchedule,
    onFormatMapped: reloadSchedule,
  });
  const canRegisterPrices = canRegisterShipmentPrices(userProfile);

  const [sort, setSort] = useState<ScheduleSort>('ETD real');
  const [mode, setMode] = useState<ScheduleModeFilter>('all');
  const [range, setRange] = useState<'window' | 'season' | null>(null);
  const [showOriginalPlan, setShowOriginalPlan] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [refreshResult, setRefreshResult] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);

  const arrivals = useMemo(
    () =>
      request.data
        ? buildArrivals(
            {
              ...request.data.data,
              cargo: request.data.data.cargo.map((box) => ({
                ...box,
                receiver: getReceiverLabel(box) || box.receiver,
              })),
            },
            startOfToday()
          )
        : [],
    [request.data, getReceiverLabel]
  );
  const { trips, withoutDates } = useMemo(
    () =>
      buildScheduleTrips(
        arrivals,
        request.data?.shipTrips ?? [],
        startOfToday()
      ),
    [arrivals, request.data?.shipTrips]
  );
  const modeTrips = useMemo(
    () => (mode === 'all' ? trips : trips.filter((trip) => trip.mode === mode)),
    [trips, mode]
  );
  const windowTrips = useMemo(
    () => tripsInWindow(modeTrips, WINDOW.from, WINDOW.to),
    [modeTrips]
  );
  // Open on the coming weeks; a past season has nothing there, so show it all.
  const activeRange = range ?? (windowTrips.length ? 'window' : 'season');
  const visibleTrips = useMemo(
    () =>
      sortScheduleTrips(
        activeRange === 'window' ? windowTrips : modeTrips,
        sort
      ),
    [activeRange, windowTrips, modeTrips, sort]
  );

  const scale = useMemo(
    () => createScale(visibleTrips, showOriginalPlan, activeRange === 'window'),
    [visibleTrips, showOriginalPlan, activeRange]
  );
  const months = useMemo(() => buildMonthBlocks(scale), [scale]);
  const weeks = useMemo(() => buildWeekMarkers(scale), [scale]);

  const oceanCount = visibleTrips.filter(
    (trip) => trip.mode === 'ocean'
  ).length;
  const airCount = visibleTrips.length - oceanCount;
  const inTransitCount = visibleTrips.filter(
    (trip) => !trip.arrived && trip.etd <= 0 && trip.eta >= 0
  ).length;
  const delayedCount = visibleTrips.filter(
    (trip) => (trip.delay ?? 0) > 0
  ).length;

  const refreshIds = [
    ...new Set(
      visibleTrips.flatMap((trip) =>
        trip.shipments.flatMap((shipment) =>
          canRefreshArrival(shipment.journey) ? [shipment.journey!.id] : []
        )
      )
    ),
  ];
  const handleRefresh = async () => {
    setRefreshResult(null);
    await request.reload(async (signal) => {
      const refresh = await refreshArrivalTracking(refreshIds, signal);
      signal.throwIfAborted();
      const result = await load(signal);
      setRefreshResult({
        succeeded: refresh.succeeded,
        failed: refresh.failures.length,
      });
      return result;
    });
  };

  const loadedAt = request.data?.loadedAt;

  return (
    <PageLayout className='h-screen overflow-y-auto'>
      <div className='mx-auto w-full max-w-[1800px]'>
        <PageHeader
          title='Cronograma de Embarques'
          titleAddon={
            <Chip size='sm' variant='flat' color='warning'>
              Beta · datos reales
            </Chip>
          }
          description='Cada nave y cada vuelo de la temporada: plan original contra fechas reales de zarpe y arribo, destino y embarques a bordo.'
          actions={
            <div className='flex flex-wrap items-center gap-3'>
              {loadedAt && (
                <span className='text-xs text-default-400'>
                  Actualizado{' '}
                  {loadedAt.toLocaleTimeString('es-CL', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
              <Button
                size='sm'
                variant='light'
                color='danger'
                isLoading={request.loading}
                isDisabled={!scope || refreshIds.length === 0}
                startContent={
                  !request.loading && <Icon icon='tabler:refresh' width={17} />
                }
                onPress={() => void handleRefresh()}
              >
                Actualizar tracking
              </Button>
            </div>
          }
        />

        {request.error && (
          <Alert color='danger' className='mt-6'>
            No se pudo cargar el cronograma: {request.error}
          </Alert>
        )}
        {refreshResult && (
          <Alert
            color={refreshResult.failed ? 'warning' : 'success'}
            className='mt-6'
          >
            Tracking: {refreshResult.succeeded} actualizado(s),{' '}
            {refreshResult.failed} con error.
          </Alert>
        )}

        {request.loading && !request.data ? (
          <LoadingState label='Cargando cronograma…' />
        ) : request.data ? (
          <>
            <Card shadow='sm' className='mt-6'>
              <CardBody className='flex flex-row flex-wrap items-center gap-x-5 gap-y-3 p-4'>
                <div className='flex gap-1 rounded-xl bg-default-100 p-1'>
                  {(
                    [
                      ['all', 'Todos'],
                      ['ocean', 'Marítimo'],
                      ['air', 'Aéreo'],
                    ] as const
                  ).map(([value, label]) => (
                    <Button
                      key={value}
                      size='sm'
                      aria-pressed={mode === value}
                      variant={mode === value ? 'solid' : 'light'}
                      color={mode === value ? 'primary' : 'default'}
                      className='h-8 min-w-0 px-3 font-semibold'
                      onPress={() => setMode(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                <div className='flex gap-1 rounded-xl bg-default-100 p-1'>
                  {(
                    [
                      ['window', 'Próximas semanas'],
                      ['season', 'Temporada completa'],
                    ] as const
                  ).map(([value, label]) => (
                    <Button
                      key={value}
                      size='sm'
                      aria-pressed={activeRange === value}
                      variant={activeRange === value ? 'solid' : 'light'}
                      color={activeRange === value ? 'primary' : 'default'}
                      className='h-8 min-w-0 px-3 font-semibold'
                      onPress={() => setRange(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
                    Ordenar por
                  </span>
                  <div className='flex gap-1 rounded-xl bg-default-100 p-1'>
                    {(['ETD real', 'ETA real', 'Atraso'] as ScheduleSort[]).map(
                      (option) => (
                        <Button
                          key={option}
                          size='sm'
                          aria-pressed={sort === option}
                          variant={sort === option ? 'solid' : 'light'}
                          color={sort === option ? 'primary' : 'default'}
                          className='h-8 min-w-0 px-3 font-semibold'
                          onPress={() => setSort(option)}
                        >
                          {option}
                        </Button>
                      )
                    )}
                  </div>
                </div>
                <Button
                  size='sm'
                  aria-pressed={showOriginalPlan}
                  variant={showOriginalPlan ? 'solid' : 'flat'}
                  color={showOriginalPlan ? 'primary' : 'default'}
                  startContent={
                    <span className='h-2 w-6 rounded border border-dashed border-default-500 bg-default-100' />
                  }
                  onPress={() => setShowOriginalPlan((value) => !value)}
                >
                  Plan original
                </Button>
                <div className='flex flex-wrap items-center gap-x-5 gap-y-2 xl:ml-auto'>
                  <LegendItem label='En tránsito' kind='transit' />
                  <LegendItem label='Arribada' kind='arrived' />
                  <LegendItem label='Sin confirmar arribo' kind='unconfirmed' />
                  <LegendItem label='Por zarpar' kind='pending' />
                  <LegendItem label='Transbordo' kind='transfer' />
                  <LegendItem label='Hoy' kind='today' />
                </div>
              </CardBody>
            </Card>

            <p className='mt-4 text-xs text-default-500'>
              {oceanCount} {oceanCount === 1 ? 'nave' : 'naves'} · {airCount}{' '}
              {airCount === 1 ? 'vuelo' : 'vuelos'} · {inTransitCount} en
              tránsito · {delayedCount} con atraso
              {withoutDates > 0 &&
                ` · ${withoutDates} ${withoutDates === 1 ? 'despacho' : 'despachos'} sin ETD o ETA`}
            </p>

            {visibleTrips.length === 0 ? (
              <EmptyState
                icon='tabler:timeline-event'
                title='Sin embarques para mostrar'
                description={
                  activeRange === 'window'
                    ? 'No hay naves ni vuelos entre tres semanas atrás y siete semanas adelante. Prueba con la temporada completa.'
                    : 'Carga despachos con nave o AWB, ETD y ETA para verlos acá.'
                }
              />
            ) : (
              <Card shadow='sm' className='mt-3'>
                <CardBody className='overflow-x-auto p-0'>
                  <div className='min-w-[1050px] sm:min-w-[1180px]'>
                    <TimelineHeader
                      scale={scale}
                      months={months}
                      weeks={weeks}
                    />
                    <div className='relative'>
                      {visibleTrips.map((trip) => (
                        <TripRow
                          key={trip.key}
                          trip={trip}
                          scale={scale}
                          showOriginalPlan={showOriginalPlan}
                          expanded={!!expanded[trip.key]}
                          onToggle={() =>
                            setExpanded((current) => ({
                              ...current,
                              [trip.key]: !current[trip.key],
                            }))
                          }
                          onOpenShipment={(shipment) =>
                            void details.handleViewDetails(
                              shipment.reference,
                              'tracking',
                              shipment.journey ? [shipment.journey] : [],
                              shipment.shipmentId
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            <p className='py-6 text-center text-xs text-default-400'>
              Fechas del tracking cuando existe; si no, las planificadas · haz
              clic en una nave o vuelo para ver sus embarques
            </p>
          </>
        ) : null}
      </div>
      <ShipmentDetailsModal
        {...details.modalProps}
        onLogbookChanged={reloadSchedule}
        getReceiverLabel={getReceiverLabel}
        priceUser={
          canRegisterPrices && userProfile?.id
            ? {
                id: userProfile.id,
                name: [userProfile.first_name, userProfile.last_name]
                  .filter(Boolean)
                  .join(' '),
              }
            : undefined
        }
      />
    </PageLayout>
  );
};

export default SchedulePage;
